import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, PackageOpen, ArrowRight, ArrowLeft, CheckCircle, Clock, MapPin, Loader2, Navigation, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAppUser from '../../../hooks/useAppUser';
import { bookingsService, outingsService } from '../../../services/jacanaDbService';
import { bookingActionsService, generateClientIdempotencyKey } from '../../../services/bookingActionsService';
import { AttendanceStatus, Booking, BookingStatus, Outing, PartialRefundStatus, PaymentStatus, RefundStatus } from '../types';
import { isValidBelgianIban, normalizeIban } from '../../../utils/iban';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ConfirmDialog from '../components/ConfirmDialog';
import BankTransferInstructions from '../components/BankTransferInstructions';
import HelpTrigger from '../../help/components/HelpTrigger';
import { getUserFacingErrorMessage } from '../../../utils/userFacingError';
import AppLoadingScreen from '../../../components/AppLoadingScreen';

interface BookingWithOuting extends Booking {
  outingDetails?: Outing | null;
  moneyTotals?: {
    currentTotalDue?: number;
    totalPaid?: number;
    totalRefundable?: number;
  };
  bookingLines?: Array<{
    rateId?: string;
    labelSnapshot?: string;
    unitPriceSnapshot?: number;
    quantityActive?: number;
  }>;
}

type ActiveBookingLine = {
  rateId: string;
  labelSnapshot: string;
  unitPriceSnapshot: number;
  quantityActive: number;
};

type PartialCancellationSelectionSummary = {
  activeLines: ActiveBookingLine[];
  cancellationByRate: Record<string, number>;
  totalParticipantsToCancel: number;
  estimatedRefund: number;
};

type BookingMoneySummary = {
  currentTotalDue: number;
  totalPaid: number;
  totalRefundable: number;
};

type PartialRefundRecordStatus = NonNullable<Booking['partialRefund']>['status'];

type PendingUserAction = {
  type: 'absence';
  booking: BookingWithOuting;
} | null;

type PartialCancellationDraft = {
  booking: BookingWithOuting;
  cancellationByRate: Record<string, number>;
  reason: string;
  idempotencyKey: string;
  iban: string;
  accountHolderName: string;
} | null;

function getActiveBookingLines(booking: Pick<BookingWithOuting, 'bookingLines'>): ActiveBookingLine[] {
  if (!Array.isArray(booking.bookingLines)) {
    return [];
  }

  return booking.bookingLines
    .map((line) => {
      const rateId = String(line?.rateId || '').trim();
      const labelSnapshot = String(line?.labelSnapshot || '').trim() || 'Tarif';
      const unitPriceSnapshot = Number(line?.unitPriceSnapshot);
      const quantityActive = Math.max(0, Math.trunc(Number(line?.quantityActive) || 0));

      if (!rateId || quantityActive <= 0 || !Number.isFinite(unitPriceSnapshot) || unitPriceSnapshot < 0) {
        return null;
      }

      return {
        rateId,
        labelSnapshot,
        unitPriceSnapshot,
        quantityActive,
      };
    })
    .filter((line): line is ActiveBookingLine => Boolean(line));
}

export function summarizePartialCancellationSelection(
  booking: Pick<BookingWithOuting, 'bookingLines'>,
  selectedCancellationByRate: Record<string, number> | null | undefined,
): PartialCancellationSelectionSummary {
  const activeLines = getActiveBookingLines(booking);
  const cancellationByRate: Record<string, number> = {};
  let totalParticipantsToCancel = 0;
  let estimatedRefund = 0;

  for (const line of activeLines) {
    const requestedQuantity = Math.max(
      0,
      Math.min(line.quantityActive, Math.trunc(Number(selectedCancellationByRate?.[line.rateId]) || 0)),
    );

    if (requestedQuantity <= 0) {
      continue;
    }

    cancellationByRate[line.rateId] = requestedQuantity;
    totalParticipantsToCancel += requestedQuantity;

    if (line.unitPriceSnapshot > 0) {
      estimatedRefund += line.unitPriceSnapshot * requestedQuantity;
    }
  }

  return {
    activeLines,
    cancellationByRate,
    totalParticipantsToCancel,
    estimatedRefund: Math.round((estimatedRefund + Number.EPSILON) * 100) / 100,
  };
}

function getBookingMoneySummary(booking: BookingWithOuting): BookingMoneySummary | null {
  const moneyTotals = booking.moneyTotals;
  if (!moneyTotals) {
    return null;
  }

  const currentTotalDue = Number(moneyTotals.currentTotalDue);
  const totalPaid = Number(moneyTotals.totalPaid);
  const totalRefundable = Number(moneyTotals.totalRefundable);

  if (!Number.isFinite(currentTotalDue) || !Number.isFinite(totalPaid) || !Number.isFinite(totalRefundable)) {
    return null;
  }

  return {
    currentTotalDue: Math.max(0, Math.round((currentTotalDue + Number.EPSILON) * 100) / 100),
    totalPaid: Math.max(0, Math.round((totalPaid + Number.EPSILON) * 100) / 100),
    totalRefundable: Math.max(0, Math.round((totalRefundable + Number.EPSILON) * 100) / 100),
  };
}

type TotalCancellationDraft = {
  booking: BookingWithOuting;
  iban: string;
  accountHolderName: string;
} | null;

type PartialRefundIbanDraft = {
  booking: BookingWithOuting;
  iban: string;
  accountHolderName: string;
} | null;

type RefundBankDetailsSectionProps = {
  estimatedAmount: number | null;
  iban: string;
  accountHolderName: string;
  onIbanChange: (value: string) => void;
  onAccountHolderNameChange: (value: string) => void;
};

function RefundBankDetailsSection({
  estimatedAmount,
  iban,
  accountHolderName,
  onIbanChange,
  onAccountHolderNameChange,
}: RefundBankDetailsSectionProps) {
  return (
    <div className="space-y-4 rounded-[20px] border border-sky-200 bg-sky-50/70 p-4">
      <div className="text-sm text-sky-900/90 leading-relaxed">
        <p className="font-semibold">Remboursement manuel</p>
        <p className="mt-1">
          Pour finaliser le remboursement, merci de renseigner votre IBAN belge et le nom du titulaire.
        </p>
        <p className="mt-2">
          Montant estimé: <strong>{estimatedAmount ?? 0} €</strong>
        </p>
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
          IBAN
        </label>
        <input
          type="text"
          value={iban}
          onChange={(event) => onIbanChange(event.target.value)}
          placeholder="BE68 5390 0754 7034"
          className="w-full rounded-[18px] border border-brand-primary/10 bg-white/80 px-4 py-3 text-sm text-brand-primary font-mono placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
        />
        {iban.trim().length > 0 && !isValidBelgianIban(iban) ? (
          <p className="text-xs text-red-600">IBAN belge invalide.</p>
        ) : (
          <p className="text-xs text-brand-primary/60">Format attendu : BE + 14 chiffres</p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
          Nom du titulaire
        </label>
        <input
          type="text"
          value={accountHolderName}
          onChange={(event) => onAccountHolderNameChange(event.target.value)}
          placeholder="Nom et prénom du titulaire"
          className="w-full rounded-[18px] border border-brand-primary/10 bg-white/80 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
        />
      </div>
    </div>
  );
}

export default function MyBookings() {
  const { id } = useParams(); // Can be present if specific booking is linked from email
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser, role, uid, loading: authLoading } = useAppUser();
  const [currentTimeMs] = useState(() => Date.now());
  const [bookings, setBookings] = useState<BookingWithOuting[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingUserAction>(null);
  const [partialCancellationDraft, setPartialCancellationDraft] = useState<PartialCancellationDraft>(null);
  const [totalCancellationDraft, setTotalCancellationDraft] = useState<TotalCancellationDraft>(null);
  const [partialRefundIbanDraft, setPartialRefundIbanDraft] = useState<PartialRefundIbanDraft>(null);
  const [cancelledBookingInfo, setCancelledBookingInfo] = useState<BookingWithOuting | null>(null);

  // Authentication Guard
  useEffect(() => {
    if (!authLoading && !firebaseUser) {
      // Redirect to login if unauthenticated, passing the current route to return after login
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [authLoading, firebaseUser, navigate, location]);

  useEffect(() => {
    async function loadBookings() {
      if (!firebaseUser?.email) return;
      setDataLoading(true);
      try {
        let userBookings: Booking[] = [];
        
        if (id) {
          // Fetch a specific booking if linked from email
          const singleBooking = await bookingsService.getById(id);
          if (singleBooking) {
            const isOwner = singleBooking.userEmail?.trim().toLowerCase() === firebaseUser.email.toLowerCase() || 
                           (singleBooking.userId && singleBooking.userId === uid);
            const isAdmin = role === 'Admin';

            if (isOwner || isAdmin) {
              userBookings = [singleBooking];
            }
          }
        } else {
          // Fetch all bookings for the user
          userBookings = await bookingsService.getByUserEmail(firebaseUser.email.trim().toLowerCase());
        }

        // Fetch outing details for each booking to get date/location/image
        const enrichedBookings = await Promise.all(
          userBookings.map(async (booking) => {
            const outing = await outingsService.getById(booking.outingId);
            return { ...booking, outingDetails: outing };
          })
        );

        setBookings(enrichedBookings);
      } catch (error) {
        console.error("Error loading bookings:", error);
      } finally {
        setDataLoading(false);
      }
    }

    if (!authLoading && firebaseUser) {
      loadBookings();
    }
  }, [id, authLoading, firebaseUser, uid, role]);

  if (authLoading) {
    return <AppLoadingScreen message="Verification de votre profil..." fullScreen />;
  }

  if (dataLoading) {
    return (
      <div className="flex justify-center items-center min-h-[100dvh] bg-brand-paper">
        <Loader2 className="w-8 h-8 animate-spin text-brand-accent" />
      </div>
    );
  }

  const resolveBookingStatus = (booking: BookingWithOuting): BookingStatus | string => {
    return booking.bookingStatus || booking.status || 'pending_validation';
  };

  const resolvePaymentStatus = (booking: BookingWithOuting) => {
    return booking.paymentStatus || (booking.isPaid ? 'paid' : 'pending');
  };

  const resolveAttendanceStatus = (booking: BookingWithOuting) => {
    return booking.attendanceStatus || 'expected';
  };

  const resolveRefundStatus = (booking: BookingWithOuting) => {
    return booking.refundStatus || 'not_applicable';
  };

  const isCancelledBooking = (booking: BookingWithOuting) => {
    return ['cancelled', 'to_be_cancelled'].includes(String(resolveBookingStatus(booking)));
  };

  const getCancelledBookingTitle = (booking: BookingWithOuting) => {
    if (booking.cancellationReason === 'organisation_cancellation') {
      return "Cette réservation a été annulée par l'organisation";
    }
    return 'Cette réservation est annulée';
  };

  const getCancelledBookingMessage = (booking: BookingWithOuting) => {
    const organizerMessage = booking.organizerCancellationMessage?.trim();
    if (organizerMessage) {
      return organizerMessage;
    }
    if (booking.cancellationReason === 'organisation_cancellation') {
      return "Le motif détaillé n'a pas été communiqué par l'organisation.";
    }
    return "Cette réservation n'est plus active. Si vous pensez qu'il s'agit d'une erreur, contactez l'équipe Jacana.";
  };

  const badgeClass = 'inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] border';
  const compactBadgeClass = 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] border';

  const getStatusBadge = (status: BookingStatus | string) => {
    switch (status) {
      case 'confirmed':
        return <span className={`${badgeClass} bg-emerald-100/50 text-emerald-700 border-emerald-200`}><CheckCircle size={12} /> Confirmé</span>;
      case 'pending_validation':
      case 'pending_payment':
      case 'pending':
      case 'authorized':
      case 'to_be_validated':
        return <span className={`${badgeClass} bg-amber-100/50 text-amber-700 border-amber-200`}><Clock size={12} /> En attente</span>;
      case 'failed':
        return <span className={`${badgeClass} bg-red-100/50 text-red-700 border-red-200`}><XCircle size={12} /> Paiement échoué</span>;
      case 'expired':
        return <span className={`${badgeClass} bg-orange-100/60 text-orange-700 border-orange-200`}><AlertTriangle size={12} /> Expiré</span>;
      case 'cancelled':
      case 'to_be_cancelled':
        return <span className={`${badgeClass} bg-red-100/50 text-red-700 border-red-200`}><Clock size={12} /> Annulé</span>;
      default:
        return <span className={`${badgeClass} bg-slate-100 text-slate-700 border-slate-200`}>{status}</span>;
    }
  };

  const getPaymentBadge = (status: string, bookingStatus?: string) => {
    if (bookingStatus === 'cancelled' && (status === 'failed' || status === 'expired')) {
      return <span className={`${compactBadgeClass} bg-orange-50 text-orange-700 border-orange-200`}>Paiement annulé</span>;
    }

    switch (status) {
      case 'paid':
        return <span className={`${compactBadgeClass} bg-emerald-50 text-emerald-700 border-emerald-200`}>Paiement reçu</span>;
      case 'refunded':
        return <span className={`${compactBadgeClass} bg-sky-50 text-sky-700 border-sky-200`}>Remboursé</span>;
      case 'failed':
        return <span className={`${compactBadgeClass} bg-red-50 text-red-700 border-red-200`}>Paiement échoué</span>;
      case 'expired':
        return <span className={`${compactBadgeClass} bg-orange-50 text-orange-700 border-orange-200`}>Paiement expiré</span>;
      default:
        return <span className={`${compactBadgeClass} bg-amber-50 text-amber-700 border-amber-200`}>Paiement en attente</span>;
    }
  };

  const getAttendanceBadge = (status: string, bookingStatus?: string) => {
    if (['cancelled', 'expired', 'failed'].includes(String(bookingStatus))) {
      return null;
    }

    switch (status) {
      case 'reported_absent':
        return <span className={`${compactBadgeClass} bg-violet-50 text-violet-700 border-violet-200`}>Absence signalée</span>;
      case 'attended':
        return <span className={`${compactBadgeClass} bg-emerald-50 text-emerald-700 border-emerald-200`}>Présence confirmée</span>;
      case 'no_show':
        return <span className={`${compactBadgeClass} bg-red-50 text-red-700 border-red-200`}>Absent constaté</span>;
      default:
        return <span className={`${compactBadgeClass} bg-slate-50 text-slate-700 border-slate-200`}>Présence attendue</span>;
    }
  };

  const getRefundBadge = (status: string) => {
    if (status === 'not_applicable') return null;
    switch (status) {
      case 'pending':
        return <span className={`${compactBadgeClass} bg-amber-50 text-amber-700 border-amber-200`}>Remboursement en cours</span>;
      case 'refunded':
        return <span className={`${compactBadgeClass} bg-sky-50 text-sky-700 border-sky-200`}>Remboursement effectué</span>;
      case 'refused':
        return <span className={`${compactBadgeClass} bg-red-50 text-red-700 border-red-200`}>Remboursement refusé</span>;
      default:
        return null;
    }
  };

  const isBookingStatusValue = (value: string | undefined): value is BookingStatus => {
    return ['pending_payment', 'pending_validation', 'confirmed', 'cancelled', 'expired', 'failed'].includes(String(value));
  };

  const isPaymentStatusValue = (value: string | undefined): value is PaymentStatus => {
    return ['pending', 'paid', 'failed', 'expired', 'refunded'].includes(String(value));
  };

  const isRefundStatusValue = (value: string | undefined): value is RefundStatus => {
    return ['not_applicable', 'pending', 'refunded', 'refused'].includes(String(value));
  };

  const isPartialRefundStatusValue = (value: string | undefined): value is PartialRefundStatus => {
    return ['not_applicable', 'pending_customer_iban', 'pending_manual', 'processed', 'refused'].includes(String(value));
  };

  const isAttendanceStatusValue = (value: string | undefined): value is AttendanceStatus => {
    return ['expected', 'reported_absent', 'attended', 'no_show'].includes(String(value));
  };

  const getBookingTimeDelta = (booking: BookingWithOuting) => {
    const outingDate = booking.outingDetails?.date ? new Date(booking.outingDetails.date) : null;
    if (!outingDate) return null;
    return outingDate.getTime() - currentTimeMs;
  };

  const canCancelBooking = (booking: BookingWithOuting) => {
    const bookingStatus = resolveBookingStatus(booking);
    const msUntilOuting = getBookingTimeDelta(booking);
    if (msUntilOuting === null) return false;
    return ['confirmed', 'pending_validation'].includes(String(bookingStatus)) && msUntilOuting > 24 * 60 * 60 * 1000;
  };

  const canReportAbsence = (booking: BookingWithOuting) => {
    const bookingStatus = resolveBookingStatus(booking);
    const attendanceStatus = resolveAttendanceStatus(booking);
    const msUntilOuting = getBookingTimeDelta(booking);
    if (msUntilOuting === null) return false;
    return bookingStatus === 'confirmed' && msUntilOuting > 0 && msUntilOuting <= 24 * 60 * 60 * 1000 && attendanceStatus !== 'reported_absent';
  };

  const getTotalParticipantsCount = (booking: BookingWithOuting) => {
    return Math.max(0, Math.trunc(Number(booking.participantsCount) || 0));
  };

  const getCancelledParticipantsCount = (booking: BookingWithOuting) => {
    const total = getTotalParticipantsCount(booking);
    const rawCancelled = Number(booking.cancelledParticipantsCount);
    if (Number.isFinite(rawCancelled)) {
      return Math.max(0, Math.min(total, Math.trunc(rawCancelled)));
    }
    return 0;
  };

  const getActiveParticipantsCount = (booking: BookingWithOuting) => {
    const total = getTotalParticipantsCount(booking);
    const rawActive = Number(booking.activeParticipantsCount);

    if (Number.isFinite(rawActive)) {
      return Math.max(0, Math.min(total, Math.trunc(rawActive)));
    }

    return Math.max(0, total - getCancelledParticipantsCount(booking));
  };

  const canCancelParticipantsPartially = (booking: BookingWithOuting) => {
    return canCancelBooking(booking)
      && getActiveParticipantsCount(booking) > 1
      && getActiveBookingLines(booking).length > 0;
  };

  const getPartialCancellationDisabledReason = (booking: BookingWithOuting) => {
    if (!canCancelBooking(booking) || getActiveParticipantsCount(booking) <= 1) {
      return null;
    }

    if (getActiveBookingLines(booking).length === 0) {
      return 'Annulation partielle indisponible: détail tarifaire manquant pour cette réservation.';
    }

    return null;
  };

  const shouldCollectRefundBankDetailsForTotal = (booking: BookingWithOuting) => {
    const moneySummary = getBookingMoneySummary(booking);
    return resolvePaymentStatus(booking) === 'paid'
      && moneySummary !== null
      && moneySummary.totalRefundable > 0;
  };

  const shouldCollectRefundBankDetailsForPartial = (booking: BookingWithOuting, cancellationByRate: Record<string, number>) => {
    const summary = summarizePartialCancellationSelection(booking, cancellationByRate);
    return resolvePaymentStatus(booking) === 'paid'
      && summary.estimatedRefund > 0;
  };

  const getRefundBankDetailsValidationError = (iban: string, accountHolderName: string) => {
    const normalizedIban = normalizeIban(iban);
    if (!isValidBelgianIban(normalizedIban)) {
      return 'IBAN belge invalide.';
    }

    if (!accountHolderName.trim()) {
      return 'Le nom du titulaire est requis.';
    }

    return null;
  };

  const getPartialRefundAmount = (booking: BookingWithOuting) => {
    const fromPartialRefund = Number(booking.partialRefund?.amount);
    if (Number.isFinite(fromPartialRefund) && fromPartialRefund > 0) {
      return fromPartialRefund;
    }

    const fromRequested = Number(booking.refundAmountRequested);
    if (Number.isFinite(fromRequested) && fromRequested > 0) {
      return fromRequested;
    }

    return null;
  };

  const getPartialRefundState = (booking: BookingWithOuting) => {
    const nestedState = String(booking.partialRefund?.status || '').trim();
    if (nestedState) return nestedState;

    const topLevelState = String(booking.partialRefundStatus || '').trim();
    if (topLevelState) return topLevelState;

    return null;
  };

  const getPartialRefundClientMessage = (booking: BookingWithOuting) => {
    const state = getPartialRefundState(booking);
    const amount = getPartialRefundAmount(booking);
    const amountLabel = amount !== null ? `${amount} €` : 'ce montant';
    const refusedReason = booking.partialRefund?.refusedReason || booking.refundReason || null;

    if (state === 'pending_customer_iban') {
      return { tone: 'warning', text: `IBAN nécessaire pour rembourser ${amountLabel}.` };
    }

    if (state === 'pending_manual') {
      return { tone: 'info', text: `Remboursement de ${amountLabel} à traiter par Jacana.` };
    }

    if (state === 'refunded' || state === 'processed') {
      return { tone: 'success', text: 'Remboursement effectué.' };
    }

    if (state === 'refused') {
      const suffix = refusedReason ? ` Motif: ${refusedReason}` : '';
      return { tone: 'danger', text: `Remboursement refusé.${suffix}` };
    }

    return null;
  };

  const canSubmitPartialRefundIban = (booking: BookingWithOuting) => {
    return getPartialRefundState(booking) === 'pending_customer_iban' && getPartialRefundAmount(booking) !== null;
  };

  const openPartialRefundIbanDialog = (booking: BookingWithOuting) => {
    setPartialRefundIbanDraft({
      booking,
      iban: '',
      accountHolderName: booking.partialRefund?.accountHolderName || booking.userName || '',
    });
  };

  const openPartialCancellationDialog = (booking: BookingWithOuting) => {
    const activeLines = getActiveBookingLines(booking);

    if (!activeLines.length) {
      toast.error('Annulation partielle indisponible: détail tarifaire manquant pour cette réservation.');
      return;
    }

    setPartialCancellationDraft({
      booking,
      cancellationByRate: {},
      reason: '',
      idempotencyKey: generateClientIdempotencyKey('pc'),
      iban: '',
      accountHolderName: booking.partialRefund?.accountHolderName || booking.userName || '',
    });
  };

  const openTotalCancellationDialog = (booking: BookingWithOuting) => {
    setTotalCancellationDraft({
      booking,
      iban: '',
      accountHolderName: booking.partialRefund?.accountHolderName || booking.userName || '',
    });
  };

  const handleCancelBooking = async () => {
    if (!totalCancellationDraft) return;

    const booking = totalCancellationDraft.booking;
    const shouldCollectRefundBankDetails = shouldCollectRefundBankDetailsForTotal(booking);
    const normalizedIban = normalizeIban(totalCancellationDraft.iban);
    const accountHolderName = totalCancellationDraft.accountHolderName.trim();

    if (shouldCollectRefundBankDetails) {
      const validationError = getRefundBankDetailsValidationError(normalizedIban, accountHolderName);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setActionLoadingId(booking.id);
    try {
      const result = await bookingActionsService.cancelBooking({ bookingId: booking.id });

      const refundAmountRequested = typeof result.refundAmountRequested === 'number'
        ? result.refundAmountRequested
        : booking.refundAmountRequested;

      const partialRefundStatus = String(result.partialRefundStatus || booking.partialRefundStatus || '').trim();
      const shouldSubmitIbanAfterCancellation =
        partialRefundStatus === 'pending_customer_iban'
        && Number(refundAmountRequested || 0) > 0;

      let nextBooking: BookingWithOuting = {
        ...booking,
        bookingStatus: isBookingStatusValue(result.bookingStatus) ? result.bookingStatus : 'cancelled',
        paymentStatus: isPaymentStatusValue(result.paymentStatus) ? result.paymentStatus : booking.paymentStatus,
        refundStatus: isRefundStatusValue(result.refundStatus) ? result.refundStatus : booking.refundStatus,
        partialRefundStatus: isPartialRefundStatusValue(result.partialRefundStatus) ? result.partialRefundStatus : booking.partialRefundStatus,
        refundAmountRequested,
        partialRefund: shouldSubmitIbanAfterCancellation
          ? {
            ...(booking.partialRefund || {}),
            status: 'pending_customer_iban',
            amount: Number(refundAmountRequested || 0) > 0
              ? Number(refundAmountRequested)
              : booking.partialRefund?.amount,
            accountHolderName: booking.partialRefund?.accountHolderName || null,
            ibanMasked: booking.partialRefund?.ibanMasked || null,
          }
          : booking.partialRefund,
        status: result.bookingStatus || 'cancelled',
      };

      if (shouldSubmitIbanAfterCancellation && shouldCollectRefundBankDetails) {
        try {
          const ibanResult = await bookingActionsService.submitPartialRefundIban({
            bookingId: booking.id,
            iban: normalizedIban,
            accountHolderName,
          });

          nextBooking = {
            ...nextBooking,
            partialRefundStatus: 'pending_manual',
            partialRefund: {
              ...(nextBooking.partialRefund || {}),
              status: 'pending_manual',
              amount: Number(refundAmountRequested || 0) > 0
                ? Number(refundAmountRequested)
                : nextBooking.partialRefund?.amount,
              accountHolderName,
              ibanMasked: ibanResult.ibanMasked || nextBooking.partialRefund?.ibanMasked || null,
            },
          };

          toast.success('Réservation annulée. Votre demande de remboursement est enregistrée.');
        } catch (ibanError: unknown) {
          toast.error(getUserFacingErrorMessage(
            ibanError,
            'Réservation annulée, mais impossible d\'enregistrer votre IBAN. Utilisez le bouton "Indiquer mon IBAN de remboursement".',
          ));
        }
      } else {
        toast.success(result.refundRequired ? "Réservation annulée. Le remboursement sera traité séparément." : "Réservation annulée.");
      }

      setBookings(prev => prev.map(item => item.id === booking.id ? nextBooking : item));
      setTotalCancellationDraft(null);
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, "Impossible d'annuler la réservation."));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReportAbsence = async (booking: BookingWithOuting) => {
    setActionLoadingId(booking.id);
    try {
      const result = await bookingActionsService.reportAbsence({ bookingId: booking.id });
      setBookings(prev => prev.map(item => item.id === booking.id ? {
        ...item,
        attendanceStatus: isAttendanceStatusValue(result.attendanceStatus) ? result.attendanceStatus : 'reported_absent'
      } : item));
      toast.success("Votre absence a bien été signalée à l'équipe Jacana.");
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, "Impossible de signaler votre absence."));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCancelParticipants = async () => {
    if (!partialCancellationDraft) return;

    const booking = partialCancellationDraft.booking;
    const selectionSummary = summarizePartialCancellationSelection(booking, partialCancellationDraft.cancellationByRate);
    const shouldCollectRefundBankDetails = shouldCollectRefundBankDetailsForPartial(booking, partialCancellationDraft.cancellationByRate);
    const normalizedIban = normalizeIban(partialCancellationDraft.iban);
    const accountHolderName = partialCancellationDraft.accountHolderName.trim();

    if (!selectionSummary.activeLines.length) {
      toast.error('Annulation partielle indisponible: détail tarifaire manquant pour cette réservation.');
      return;
    }

    if (selectionSummary.totalParticipantsToCancel <= 0) {
      toast.error('Sélectionnez au moins une ligne tarifaire à annuler.');
      return;
    }

    if (shouldCollectRefundBankDetails) {
      const validationError = getRefundBankDetailsValidationError(normalizedIban, accountHolderName);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    setActionLoadingId(booking.id);

    try {
      const result = await bookingActionsService.cancelBookingParticipants({
        bookingId: booking.id,
        cancellationByRate: selectionSummary.cancellationByRate,
        idempotencyKey: partialCancellationDraft.idempotencyKey,
        reason: partialCancellationDraft.reason.trim() || undefined,
      });

      const shouldSubmitIbanAfterCancellation =
        result.partialRefundStatus === 'pending_customer_iban'
        && Number(result.refundAmountRequested || 0) > 0;

      if (shouldSubmitIbanAfterCancellation && shouldCollectRefundBankDetails) {
        try {
          const ibanResult = await bookingActionsService.submitPartialRefundIban({
            bookingId: booking.id,
            iban: normalizedIban,
            accountHolderName,
          });

          setBookings((prev) => prev.map((item) => {
            if (item.id !== booking.id) return item;

            return {
              ...item,
              partialRefundStatus: 'pending_manual',
              partialRefund: {
                ...(item.partialRefund || {}),
                status: 'pending_manual',
                amount: typeof result.refundAmountRequested === 'number'
                  ? result.refundAmountRequested
                  : item.partialRefund?.amount,
                accountHolderName,
                ibanMasked: ibanResult.ibanMasked || item.partialRefund?.ibanMasked || null,
              },
            };
          }));

          if (result.becameFullyCancelled) {
            toast.success('Tous les participants ont été annulés. Votre demande de remboursement est enregistrée.');
          } else {
            toast.success(`${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s). Votre demande de remboursement est enregistrée.`);
          }
        } catch (ibanError: unknown) {
          toast.error(getUserFacingErrorMessage(
            ibanError,
            'Annulation effectuée, mais impossible d\'enregistrer votre IBAN. Utilisez le bouton "Indiquer mon IBAN de remboursement".',
          ));
        }
      }

      try {
        const refreshedBooking = await bookingsService.getById(booking.id);

        if (refreshedBooking) {
          const refreshedOuting = await outingsService.getById(refreshedBooking.outingId);
          setBookings((prev) => prev.map((item) => item.id === booking.id
            ? { ...refreshedBooking, outingDetails: refreshedOuting }
            : item));
        }
      } catch (refreshError) {
        console.error('Error refreshing booking after partial cancellation:', refreshError);
      }

      if (shouldSubmitIbanAfterCancellation && shouldCollectRefundBankDetails) {
        if (result.becameFullyCancelled) {
          toast.success('Tous les participants sélectionnés ont été annulés. Votre demande de remboursement est enregistrée.');
        } else {
          toast.success(`${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s). Votre demande de remboursement est enregistrée.`);
        }
      } else if (result.becameFullyCancelled) {
        toast.success(result.refundRequired
          ? 'Tous les participants ont été annulés. Un remboursement reste à traiter.'
          : 'Tous les participants ont été annulés.');
      } else {
        toast.success(result.refundRequired
          ? `${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s). Un remboursement reste à traiter.`
          : `${selectionSummary.totalParticipantsToCancel} participant(s) annulé(s).`);
      }

      setPartialCancellationDraft(null);
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, "Impossible d'annuler ces participants."));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSubmitPartialRefundIban = async () => {
    if (!partialRefundIbanDraft) return;

    const booking = partialRefundIbanDraft.booking;
    const normalizedIban = normalizeIban(partialRefundIbanDraft.iban);
    const accountHolderName = partialRefundIbanDraft.accountHolderName.trim();

    if (!isValidBelgianIban(normalizedIban)) {
      toast.error('IBAN belge invalide.');
      return;
    }

    if (!accountHolderName) {
      toast.error('Le nom du titulaire est requis.');
      return;
    }

    setActionLoadingId(booking.id);
    try {
      const result = await bookingActionsService.submitPartialRefundIban({
        bookingId: booking.id,
        iban: normalizedIban,
        accountHolderName,
      });

      setBookings((prev) => prev.map((item) => {
        if (item.id !== booking.id) return item;

        return {
          ...item,
          partialRefundStatus: 'pending_manual',
          partialRefund: {
            ...(item.partialRefund || {}),
            status: 'pending_manual',
            amount: getPartialRefundAmount(item) ?? undefined,
            accountHolderName,
            ibanMasked: result.ibanMasked || item.partialRefund?.ibanMasked || null,
          },
        };
      }));

      setPartialRefundIbanDraft(null);
      toast.success('Votre demande de remboursement est enregistrée.');
    } catch (error: unknown) {
      toast.error(getUserFacingErrorMessage(error, 'Impossible d\'enregistrer votre IBAN de remboursement.'));
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 bg-brand-paper">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-16">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-4xl md:text-5xl font-serif font-bold italic text-brand-primary">
                {id ? "Détails de Réservation" : "Mes Réservations"}
              </h1>
              <HelpTrigger
                articleId="booking.page.my_bookings"
                category="booking.my_bookings"
                label="Aide sur mes réservations"
              />
            </div>
            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.24em] opacity-55 text-brand-primary font-sans">
              Connecté en tant que {firebaseUser?.email}
            </p>
          </div>
          {id && (
            <button 
              onClick={() => navigate('/mes-reservations')}
              className="flex items-center gap-3 text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-accent hover:opacity-70 transition-opacity min-h-[44px]"
            >
              Voir Tout <ArrowRight size={14} />
            </button>
          )}
        </div>

        {bookings.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center p-24 bg-white rounded-[40px] border border-brand-primary/5 shadow-xl text-center"
          >
            <div className="w-24 h-24 bg-brand-paper rounded-full flex items-center justify-center mb-8 border border-brand-primary/10">
              <PackageOpen size={40} className="text-brand-primary/30" />
            </div>
            <h3 className="text-2xl font-serif italic text-brand-primary mb-4">Aucune réservation</h3>
            <p className="text-brand-primary/50 mb-8 font-sans">Vous n'avez pas encore réservé d'expédition.</p>
            <button onClick={() => navigate('/')} className="btn-premium">Découvrir les expéditions</button>
          </motion.div>
        ) : (
          <div className="space-y-8">
            <AnimatePresence>
              {bookings.map((booking, index) => (
                <motion.div 
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 md:p-10 rounded-[40px] border border-brand-primary/5 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-500 overflow-hidden relative"
                >
                  <div className="absolute right-0 top-0 w-32 h-32 bg-brand-primary/5 rounded-bl-full -mr-8 -mt-8 pointer-events-none" />
                  
                  <div className="flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
                    <div className="flex-1 space-y-6 relative z-10 w-full">
                      <div className="flex flex-wrap items-center gap-3">
                        {getStatusBadge(resolveBookingStatus(booking))}
                        {getPaymentBadge(resolvePaymentStatus(booking), resolveBookingStatus(booking))}
                        {getAttendanceBadge(resolveAttendanceStatus(booking), resolveBookingStatus(booking))}
                        {getRefundBadge(resolveRefundStatus(booking))}
                        <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/50">
                          Réf: {booking.id}
                        </span>
                      </div>
                      
                      <h3 className="text-3xl font-serif font-bold italic text-brand-primary leading-tight">
                        {booking.outingTitle}
                      </h3>

                      {isCancelledBooking(booking) ? (
                        <div className="rounded-[28px] border border-red-200 bg-red-50/80 px-5 py-4">
                          <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-red-700">
                            Réservation annulée
                          </p>
                          <p className="mt-2 text-sm md:text-base leading-relaxed text-red-900/80">
                            {booking.cancellationReason === 'organisation_cancellation'
                              ? "Cette sortie a été annulée par l'organisation."
                              : "Cette réservation a été annulée et n'ouvre plus l'accès au détail de la sortie."}
                          </p>
                        </div>
                      ) : null}

                      {getPartialRefundClientMessage(booking) ? (
                        <div
                          className={`rounded-[20px] border px-4 py-3 text-sm leading-relaxed ${
                            getPartialRefundClientMessage(booking)?.tone === 'danger'
                              ? 'border-red-200 bg-red-50/80 text-red-900/85'
                              : getPartialRefundClientMessage(booking)?.tone === 'success'
                                ? 'border-sky-200 bg-sky-50/80 text-sky-900/85'
                                : getPartialRefundClientMessage(booking)?.tone === 'info'
                                  ? 'border-amber-200 bg-amber-50/80 text-amber-900/85'
                                  : 'border-orange-200 bg-orange-50/80 text-orange-900/85'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p>{getPartialRefundClientMessage(booking)?.text}</p>
                            <HelpTrigger
                              articleId="booking.partial_refund.status"
                              category="booking.partial_refund"
                              label="Aide sur le statut de remboursement partiel"
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="grid grid-cols-2 lg:flex lg:flex-row gap-8 pt-4">
                        {booking.outingDetails && (
                          <>
                            <div className="flex items-center gap-3">
                              <Calendar size={18} className="text-brand-accent/60" />
                              <div className="space-y-1">
                                <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Date</p>
                                <p className="font-serif italic text-brand-primary/90">
                                  {format(new Date(booking.outingDetails.date), 'dd MMM yyyy', { locale: fr })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <MapPin size={18} className="text-brand-accent/60" />
                              <div className="space-y-1">
                                <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Lieu</p>
                                <p className="font-serif italic text-brand-primary/90">{booking.outingDetails.location}</p>
                              </div>
                            </div>
                          </>
                        )}
                        <div className="flex items-center gap-3">
                          <PackageOpen size={18} className="text-brand-accent/60" />
                          <div className="space-y-1">
                            <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.16em] text-brand-primary/50">Places</p>
                            <p className="font-serif italic text-brand-primary/90">{getActiveParticipantsCount(booking)} participants actifs sur {getTotalParticipantsCount(booking)}</p>
                          </div>
                        </div>
                      </div>

                      {booking.paymentMethod === 'bank_transfer' && booking.bankTransferCommunication ? (
                        <div className="pt-3">
                          <BankTransferInstructions
                            iban={booking.bankTransferIban || null}
                            amount={typeof booking.amount === 'number' ? booking.amount : 0}
                            reference={booking.bankTransferCommunication}
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="w-full md:w-auto flex flex-col items-center md:items-end space-y-3 pt-6 md:pt-0 border-t md:border-t-0 border-brand-primary/5">
                       {canReportAbsence(booking) ? (
                         <HelpTrigger
                           articleId="booking.action.report_absence"
                           category="booking.action"
                           label="Aide sur le signalement d'absence"
                         />
                       ) : canCancelParticipantsPartially(booking) ? (
                         <HelpTrigger
                           articleId="booking.action.cancel_participants"
                           category="booking.action"
                           label="Aide sur l'annulation partielle"
                         />
                       ) : null}

                       {booking.outingDetails?.meetingPoint && resolveBookingStatus(booking) === 'confirmed' ? (
                          <a 
                            href={booking.outingDetails.meetingPoint}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-brand-primary text-white rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] hover:bg-brand-accent hover:shadow-xl hover:shadow-brand-accent/20 transition-all duration-300 min-h-[44px]"
                          >
                            <Navigation size={14} /> Plan d'accès
                          </a>
                       ) : isCancelledBooking(booking) ? (
                         <button 
                           onClick={() => setCancelledBookingInfo(booking)}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 border border-red-200 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-red-700 bg-red-50 hover:bg-red-100 transition-all duration-300 min-h-[44px]"
                         >
                           Voir l'annulation
                         </button>
                       ) : (
                         <button 
                           onClick={() => navigate(`/outing/${booking.outingId}`)}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 border border-brand-primary/10 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-brand-primary hover:border-brand-primary/30 hover:bg-brand-paper transition-all duration-300 min-h-[44px]"
                          >
                           Détails sortie
                         </button>
                       )}

                       {canCancelParticipantsPartially(booking) && (
                         <button
                           onClick={() => openPartialCancellationDialog(booking)}
                           disabled={actionLoadingId === booking.id}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-orange-50 border border-orange-100 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-orange-700 hover:bg-orange-100 transition-all duration-300 disabled:opacity-60 min-h-[44px]"
                         >
                           {actionLoadingId === booking.id ? 'TRAITEMENT...' : 'Annuler des participants'}
                         </button>
                       )}

                       {!canCancelParticipantsPartially(booking) && getPartialCancellationDisabledReason(booking) ? (
                         <div className="w-full md:w-auto space-y-2">
                           <button
                             type="button"
                             disabled
                             className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-orange-50 border border-orange-100 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-orange-700/60 opacity-70 cursor-not-allowed min-h-[44px]"
                           >
                             Annulation partielle indisponible
                           </button>
                           <p className="max-w-xs text-xs text-orange-800/80 text-center md:text-right">
                             {getPartialCancellationDisabledReason(booking)}
                           </p>
                         </div>
                       ) : null}

                       {canSubmitPartialRefundIban(booking) && (
                         <button
                           onClick={() => openPartialRefundIbanDialog(booking)}
                           disabled={actionLoadingId === booking.id}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-sky-50 border border-sky-100 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-sky-700 hover:bg-sky-100 transition-all duration-300 disabled:opacity-60 min-h-[44px]"
                         >
                           {actionLoadingId === booking.id ? 'TRAITEMENT...' : 'Indiquer mon IBAN de remboursement'}
                         </button>
                       )}

                       {canCancelBooking(booking) && (
                         <button
                           onClick={() => openTotalCancellationDialog(booking)}
                           disabled={actionLoadingId === booking.id}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-red-50 border border-red-100 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-red-700 hover:bg-red-100 transition-all duration-300 disabled:opacity-60 min-h-[44px]"
                         >
                           {actionLoadingId === booking.id ? 'TRAITEMENT...' : 'Annuler toute la réservation'}
                         </button>
                       )}

                       {canReportAbsence(booking) && (
                         <button
                           onClick={() => setPendingAction({ type: 'absence', booking })}
                           disabled={actionLoadingId === booking.id}
                           className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-violet-50 border border-violet-100 rounded-full text-[11px] md:text-xs font-bold uppercase tracking-[0.22em] text-violet-700 hover:bg-violet-100 transition-all duration-300 disabled:opacity-60 min-h-[44px]"
                         >
                           {actionLoadingId === booking.id ? 'TRAITEMENT...' : 'Je ne viendrai pas'}
                         </button>
                       )}

                       {!canCancelBooking(booking) && resolveBookingStatus(booking) === 'confirmed' && !canReportAbsence(booking) && (() => {
                         const msUntilOuting = getBookingTimeDelta(booking);
                         return msUntilOuting !== null && msUntilOuting <= 24 * 60 * 60 * 1000 && msUntilOuting > 0;
                       })() && (
                         <p className="max-w-xs text-center md:text-right text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-brand-primary/55 leading-relaxed">
                           L'annulation standard n'est plus disponible à moins de 24h avant la sortie.
                         </p>
                       )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        <ConfirmDialog
          open={Boolean(pendingAction?.type === 'absence')}
          title="Signaler votre absence ?"
          message="Votre absence sera transmise à l'équipe Jacana pour cette sortie."
          confirmLabel="Confirmer le signalement"
          tone='primary'
          isLoading={Boolean(pendingAction?.booking?.id && actionLoadingId === pendingAction.booking.id)}
          onClose={() => setPendingAction(null)}
          onConfirm={async () => {
            if (!pendingAction) return;
            await handleReportAbsence(pendingAction.booking);
            setPendingAction(null);
          }}
        />
        <ConfirmDialog
          open={Boolean(totalCancellationDraft)}
          title="Confirmer l'annulation ?"
          message="Cette réservation sera annulée selon les règles Jacana et libérera les places si l'annulation reste autorisée."
          confirmLabel="Confirmer"
          tone="danger"
          isLoading={Boolean(totalCancellationDraft?.booking?.id && actionLoadingId === totalCancellationDraft.booking.id)}
          onConfirm={handleCancelBooking}
          onClose={() => {
            if (actionLoadingId) return;
            setTotalCancellationDraft(null);
          }}
        >
          {totalCancellationDraft ? (() => {
            const moneySummary = getBookingMoneySummary(totalCancellationDraft.booking);

            if (!moneySummary) {
              return (
                <p className="text-sm text-brand-primary/70">
                  Détail financier indisponible pour cette réservation.
                </p>
              );
            }

            return (
              <p className="text-sm text-brand-primary/70">
                Montant remboursable estimé: <strong>{moneySummary.totalRefundable} €</strong>{' '}
                • Montant dû courant: <strong>{moneySummary.currentTotalDue} €</strong>{' '}
                • Montant payé: <strong>{moneySummary.totalPaid} €</strong>
              </p>
            );
          })() : null}
          {totalCancellationDraft && shouldCollectRefundBankDetailsForTotal(totalCancellationDraft.booking) ? (
            <RefundBankDetailsSection
              estimatedAmount={getBookingMoneySummary(totalCancellationDraft.booking)?.totalRefundable ?? null}
              iban={totalCancellationDraft.iban}
              accountHolderName={totalCancellationDraft.accountHolderName}
              onIbanChange={(value) => {
                setTotalCancellationDraft((prev) => prev ? {
                  ...prev,
                  iban: value,
                } : prev);
              }}
              onAccountHolderNameChange={(value) => {
                setTotalCancellationDraft((prev) => prev ? {
                  ...prev,
                  accountHolderName: value,
                } : prev);
              }}
            />
          ) : null}
        </ConfirmDialog>
        <ConfirmDialog
          open={Boolean(cancelledBookingInfo)}
          title={cancelledBookingInfo ? getCancelledBookingTitle(cancelledBookingInfo) : ''}
          message={cancelledBookingInfo ? getCancelledBookingMessage(cancelledBookingInfo) : ''}
          confirmLabel={null}
          cancelLabel="Fermer"
          tone="danger"
          onConfirm={() => setCancelledBookingInfo(null)}
          onClose={() => setCancelledBookingInfo(null)}
        />
        <ConfirmDialog
          open={Boolean(partialCancellationDraft)}
          title="Annuler des participants ?"
          message={partialCancellationDraft
            ? "Cette action met à jour automatiquement la réservation selon les règles Jacana."
            : ''}
          confirmLabel={partialCancellationDraft && summarizePartialCancellationSelection(
            partialCancellationDraft.booking,
            partialCancellationDraft.cancellationByRate,
          ).totalParticipantsToCancel > 0 ? 'Confirmer' : null}
          tone="danger"
          isLoading={Boolean(partialCancellationDraft?.booking?.id && actionLoadingId === partialCancellationDraft.booking.id)}
          onConfirm={handleCancelParticipants}
          onClose={() => {
            if (actionLoadingId) return;
            setPartialCancellationDraft(null);
          }}
        >
          {partialCancellationDraft ? (
            <div className="space-y-4">
              {(() => {
                const summary = summarizePartialCancellationSelection(
                  partialCancellationDraft.booking,
                  partialCancellationDraft.cancellationByRate,
                );

                return (
                  <>
                    <div className="space-y-3">
                      <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                        Lignes tarifaires actives
                      </label>

                      {summary.activeLines.map((line) => {
                        const selectedQuantity = partialCancellationDraft.cancellationByRate[line.rateId] ?? 0;

                        return (
                          <div
                            key={line.rateId}
                            className="flex flex-col gap-3 rounded-[18px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 md:flex-row md:items-center md:justify-between"
                          >
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-brand-primary">{line.labelSnapshot}</p>
                              <p className="text-xs text-brand-primary/65">
                                {line.quantityActive} actif(s) • {line.unitPriceSnapshot > 0 ? `${line.unitPriceSnapshot} € / participant` : 'Gratuit'}
                              </p>
                            </div>

                            <div className="w-full md:w-28">
                              <input
                                type="number"
                                min={0}
                                max={line.quantityActive}
                                value={selectedQuantity}
                                onChange={(event) => {
                                  const nextValue = Number.parseInt(event.target.value, 10);
                                  const normalized = Number.isFinite(nextValue)
                                    ? Math.max(0, Math.min(line.quantityActive, nextValue))
                                    : 0;

                                  setPartialCancellationDraft((prev) => {
                                    if (!prev) return prev;

                                    const nextCancellationByRate = { ...prev.cancellationByRate };
                                    if (normalized > 0) {
                                      nextCancellationByRate[line.rateId] = normalized;
                                    } else {
                                      delete nextCancellationByRate[line.rateId];
                                    }

                                    return {
                                      ...prev,
                                      cancellationByRate: nextCancellationByRate,
                                    };
                                  });
                                }}
                                className="w-full rounded-[18px] border border-brand-primary/10 bg-white px-4 py-3 text-sm text-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="rounded-[18px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary/80 leading-relaxed space-y-2">
                      <p>
                        Résumé: {summary.totalParticipantsToCancel} participant(s) annulé(s),{' '}
                        {Math.max(0, getActiveParticipantsCount(partialCancellationDraft.booking) - summary.totalParticipantsToCancel)} actif(s) restant(s)
                        {' '}sur {getTotalParticipantsCount(partialCancellationDraft.booking)}.
                      </p>
                      <p>
                        Remboursement estimé: <strong>{summary.estimatedRefund} €</strong>
                      </p>
                      {summary.totalParticipantsToCancel <= 0 ? (
                        <p className="text-red-700 font-semibold">
                          Sélectionnez au moins une ligne tarifaire à annuler.
                        </p>
                      ) : null}
                      {summary.totalParticipantsToCancel >= getActiveParticipantsCount(partialCancellationDraft.booking) ? (
                        <p className="text-red-700 font-semibold">
                          Cette sélection annulera toute la réservation.
                        </p>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                        Raison (optionnelle)
                      </label>
                      <textarea
                        rows={3}
                        maxLength={300}
                        value={partialCancellationDraft.reason}
                        onChange={(event) => {
                          const nextReason = event.target.value;
                          setPartialCancellationDraft((prev) => prev ? { ...prev, reason: nextReason } : prev);
                        }}
                        placeholder="Ex: 2 participants indisponibles"
                        className="w-full rounded-[18px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                      />
                    </div>

                    {shouldCollectRefundBankDetailsForPartial(partialCancellationDraft.booking, partialCancellationDraft.cancellationByRate) ? (
                      <RefundBankDetailsSection
                        estimatedAmount={summary.estimatedRefund}
                        iban={partialCancellationDraft.iban}
                        accountHolderName={partialCancellationDraft.accountHolderName}
                        onIbanChange={(value) => {
                          setPartialCancellationDraft((prev) => prev ? {
                            ...prev,
                            iban: value,
                          } : prev);
                        }}
                        onAccountHolderNameChange={(value) => {
                          setPartialCancellationDraft((prev) => prev ? {
                            ...prev,
                            accountHolderName: value,
                          } : prev);
                        }}
                      />
                    ) : null}
                  </>
                );
              })()}
            </div>
          ) : null}
        </ConfirmDialog>

        <ConfirmDialog
          open={Boolean(partialRefundIbanDraft)}
          title="Indiquer mon IBAN de remboursement"
          message="Cet IBAN sera utilisé uniquement pour ce remboursement partiel."
          confirmLabel="Continuer"
          tone="primary"
          isLoading={Boolean(partialRefundIbanDraft?.booking?.id && actionLoadingId === partialRefundIbanDraft.booking.id)}
          onConfirm={handleSubmitPartialRefundIban}
          onClose={() => {
            if (actionLoadingId) return;
            setPartialRefundIbanDraft(null);
          }}
        >
          {partialRefundIbanDraft ? (
            <div className="space-y-4">
              <div className="flex items-center justify-end">
                <HelpTrigger
                  articleId="booking.partial_refund.customer_iban"
                  category="booking.partial_refund"
                  label="Aide sur la saisie d'IBAN"
                />
              </div>

              <div className="rounded-[18px] border border-sky-200 bg-sky-50/70 px-4 py-3 text-sm text-sky-900/85">
                Montant à rembourser: <strong>{getPartialRefundAmount(partialRefundIbanDraft.booking) ?? 0} €</strong>
              </div>

              <div className="space-y-2">
                <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                  IBAN
                </label>
                <input
                  type="text"
                  value={partialRefundIbanDraft.iban}
                  onChange={(event) => {
                    const nextIban = event.target.value;
                    setPartialRefundIbanDraft((prev) => prev ? {
                      ...prev,
                      iban: nextIban,
                    } : prev);
                  }}
                  placeholder="BE68 5390 0754 7034"
                  className="w-full rounded-[18px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary font-mono placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                />
                {partialRefundIbanDraft.iban.trim().length > 0 && !isValidBelgianIban(partialRefundIbanDraft.iban) ? (
                  <p className="text-xs text-red-600">IBAN belge invalide.</p>
                ) : (
                  <p className="text-xs text-brand-primary/60">Format attendu : BE + 14 chiffres</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-brand-primary/60">
                  Nom du titulaire
                </label>
                <input
                  type="text"
                  value={partialRefundIbanDraft.accountHolderName}
                  onChange={(event) => {
                    const nextName = event.target.value;
                    setPartialRefundIbanDraft((prev) => prev ? {
                      ...prev,
                      accountHolderName: nextName,
                    } : prev);
                  }}
                  placeholder="Nom et prénom du titulaire"
                  className="w-full rounded-[18px] border border-brand-primary/10 bg-brand-paper/40 px-4 py-3 text-sm text-brand-primary placeholder:text-brand-primary/35 focus:outline-none focus:ring-2 focus:ring-brand-accent/30"
                />
              </div>

              <p className="text-xs text-brand-primary/60 leading-relaxed">
                Par sécurité, votre IBAN complet n'est jamais affiché dans votre espace après envoi.
              </p>
            </div>
          ) : null}
        </ConfirmDialog>
      </div>
    </div>
  );
}
